import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type {
  ApiEnvelope,
  CreateParentInviteRequest,
  CreateParentInviteResponse,
  LinkedParent,
  ParentInvite,
} from "@/lib/api-types";

/**
 * Student-side hooks for the "Linked parents" Settings card. Every
 * query/mutation mirrors a /students/me/parent* route in
 * backend/functions/staxed_parent_handler/index.mjs.
 */

/** GET /students/me/parent-invites -> my pending invites. */
export function useMyParentInvites(enabled = true) {
  return useQuery({
    queryKey: qk.studentParent.invites(),
    enabled,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ParentInvite[]>>("/students/me/parent-invites");
      return unwrap(res.data) ?? [];
    },
    staleTime: 15_000,
  });
}

/** POST /students/me/parent-invites -> create an invite (email and/or share link). */
export function useCreateParentInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CreateParentInviteRequest) => {
      const res = await api.post<ApiEnvelope<CreateParentInviteResponse>>(
        "/students/me/parent-invites",
        vars,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.studentParent.invites() });
    },
  });
}

/** DELETE /students/me/parent-invites/{invite_code} -> revoke a pending invite. */
export function useRevokeParentInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invite_code: string) => {
      const res = await api.delete<ApiEnvelope<{ ok: boolean }>>(
        `/students/me/parent-invites/${encodeURIComponent(invite_code)}`,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.studentParent.invites() });
    },
  });
}

/** GET /students/me/parents -> my linked parents. */
export function useMyParents(enabled = true) {
  return useQuery({
    queryKey: qk.studentParent.parents(),
    enabled,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<LinkedParent[]>>("/students/me/parents");
      return unwrap(res.data) ?? [];
    },
    staleTime: 15_000,
  });
}

/** DELETE /students/me/parents/{parent_user_id} -> revoke a linked parent from the student side. */
export function useRevokeLinkedParent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (parent_user_id: string) => {
      const res = await api.delete<ApiEnvelope<{ ok: boolean }>>(
        `/students/me/parents/${encodeURIComponent(parent_user_id)}`,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.studentParent.parents() });
    },
  });
}
